import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  IconButton,
  Stack,
  Alert,
  Fade,
  Zoom,
  Divider,
  Tooltip
} from '@mui/material';
import { 
  AddCircleOutline, 
  DeleteOutline, 
  HelpOutline,
  CheckCircleOutline
} from '@mui/icons-material';
import { styled } from '@mui/system';

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: "1rem !important",
  "& label.Mui-focused": {
    color: "#5c6bc0",
  },
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": {
      borderColor: "#5c6bc0",
      borderWidth: "2px",
    },
    "&:hover fieldset": {
      borderColor: "#7986cb",
    },
  },
}));

const FAQSchemaForm = ({ onGenerate }) => {
  const validationSchema = Yup.object({
    mainEntity: Yup.array()
      .of(
        Yup.object({
          question: Yup.string().required('Question is required'),
          acceptedAnswer: Yup.object({ 
            text: Yup.string().required('Answer is required') 
          })
        })
      )
      .min(1, 'Add at least one question')
  });

  const formik = useFormik({
    initialValues: { mainEntity: [{ question: '', acceptedAnswer: { text: '' } }] },
    validationSchema,
    onSubmit: values => {
      const schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: values.mainEntity.map(({ question, acceptedAnswer: { text } }) => ({
          "@type": "Question",
          name: question,
          acceptedAnswer: { "@type": "Answer", text }
        }))
      };
      onGenerate(schema, 'faq');
    }
  });

  const addQuestion = () => 
    formik.setFieldValue('mainEntity', [...formik.values.mainEntity, { 
      question: '', acceptedAnswer: { text: '' } 
    }]);

  const removeQuestion = index => 
    formik.setFieldValue('mainEntity', 
      formik.values.mainEntity.filter((_, i) => i !== index));

  return (
    <Fade in={true} timeout={500}>
      <Box className="form-container">
        <Card elevation={3} className="form-card">
          <CardContent>
            <Typography variant="h4" component="h1" className="form-title" gutterBottom>
              FAQ Schema Generator
              <Tooltip title="Generate structured data markup for your FAQ pages">
                <HelpOutline color="action" className="info-icon" />
              </Tooltip>
            </Typography>
            
            <Typography variant="subtitle1" color="textSecondary" paragraph>
              Create SEO-friendly FAQ schema markup by adding your questions and answers
            </Typography>

            <Divider className="divider" />

            <Box component="form" onSubmit={formik.handleSubmit}>
              <Stack spacing={3}>
                {formik.values.mainEntity.map((item, index) => (
                  <Zoom in={true} key={index}>
                    <Card variant="outlined" className="question-card">
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="subtitle2" color="primary">
                            Question #{index + 1}
                          </Typography>
                          {formik.values.mainEntity.length > 1 && (
                            <IconButton
                              size="small"
                              onClick={() => removeQuestion(index)}
                              className="delete-question"
                              aria-label="delete question"
                            >
                              <DeleteOutline color="error" />
                            </IconButton>
                          )}
                        </Box>

                        <StyledTextField
                          fullWidth
                          name={`mainEntity[${index}].question`}
                          label="Question"
                          value={item.question}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={Boolean(
                            formik.touched.mainEntity?.[index]?.question &&
                            formik.errors.mainEntity?.[index]?.question
                          )}
                          helperText={
                            formik.touched.mainEntity?.[index]?.question &&
                            formik.errors.mainEntity?.[index]?.question
                          }
                          variant="outlined"
                          size="medium"
                        />

                        <StyledTextField
                          fullWidth
                          multiline
                          minRows={3}
                          name={`mainEntity[${index}].acceptedAnswer.text`}
                          label="Answer"
                          value={item.acceptedAnswer.text}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={Boolean(
                            formik.touched.mainEntity?.[index]?.acceptedAnswer?.text &&
                            formik.errors.mainEntity?.[index]?.acceptedAnswer?.text
                          )}
                          helperText={
                            formik.touched.mainEntity?.[index]?.acceptedAnswer?.text &&
                            (typeof formik.errors.mainEntity?.[index]?.acceptedAnswer?.text === 'string' ? 
                              formik.errors.mainEntity?.[index]?.acceptedAnswer?.text : '')
                          }
                          variant="outlined"
                          size="medium"
                        />
                      </CardContent>
                    </Card>
                  </Zoom>
                ))}

                <Fade in={true}>
                  <Button
                    startIcon={<AddCircleOutline />}
                    onClick={addQuestion}
                    variant="outlined"
                    color="primary"
                    className="add-button"
                  >
                    Add Another Question
                  </Button>
                </Fade>

                {formik.submitCount > 0 && formik.errors.mainEntity && typeof formik.errors.mainEntity === 'string' && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {formik.errors.mainEntity}
                  </Alert>
                )}

                <Box mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    type="submit"
                    className="submit-button"
                    disabled={!formik.isValid || !formik.dirty}
                    startIcon={<CheckCircleOutline />}
                    fullWidth
                  >
                    Generate FAQ Schema
                  </Button>
                </Box>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
};

export default FAQSchemaForm;